// src/Context/SignalRContext.jsx
import { createContext, useContext, useCallback, useEffect, useRef, useState } from "react";
import Cookies from "js-cookie";
import * as signalR from "@microsoft/signalr";
import { createNotificationConnection } from "../Services/NotificationService";

const SignalRContext = createContext(null);

export const SignalRProvider = ({ children }) => {
    const [notifications, setNotifications] = useState([]);

    const [connectionStatusVersion, setConnectionStatusVersion] = useState(0);
    const [chatMessageVersion, setChatMessageVersion] = useState(0);
    const [latestChatMessage, setLatestChatMessage] = useState(null);

    const [typingIndicatorVersion, setTypingIndicatorVersion] = useState(0);
    const [latestTypingIndicator, setLatestTypingIndicator] = useState(null);

    const [messageSeenVersion, setMessageSeenVersion] = useState(0);
    const [latestMessageSeen, setLatestMessageSeen] = useState(null);

    const [messagesDeletedVersion, setMessagesDeletedVersion] = useState(0);
    const [latestMessagesDeleted, setLatestMessagesDeleted] = useState(null);


    // ── Call signal states ───────────────────────────────────────────
    const [incomingCall, setIncomingCall] = useState(null);
    const [callAnswerVersion, setCallAnswerVersion] = useState(0);
    const [latestCallAnswer, setLatestCallAnswer] = useState(null);
    const [iceCandidateVersion, setIceCandidateVersion] = useState(0);
    const [latestIceCandidate, setLatestIceCandidate] = useState(null);
    const [iceCandidates, setIceCandidates] = useState([]);
    const [callEndedVersion, setCallEndedVersion] = useState(0);
    const [latestCallEnded, setLatestCallEnded] = useState(null);
    const [callLogVersion, setCallLogVersion] = useState(0);
    const [latestCallLog, setLatestCallLog] = useState(null);
    const [latestCallOffer, setLatestCallOffer] = useState(null);
    // ────────────────────────────────────────────────────────────────

    const [moderationCheckVersion, setModerationCheckVersion] = useState(0);
    const [latestModerationCheck, setLatestModerationCheck] = useState(null);
    const [token, setToken] = useState(() => Cookies.get("token"));

    // const [isConnected, setIsConnected] = useState(false);

    const connectionRef = useRef(null);

    // const isStoppingRef = useRef(false);

    useEffect(() => {
        const handleAuthChange = () => {
            setToken(Cookies.get("token") ?? null);
        };

        window.addEventListener("auth-change", handleAuthChange);
        return () => window.removeEventListener("auth-change", handleAuthChange);
    }, []);

    useEffect(() => {
        if (!token) {
            // Clean up if logged out
            if (connectionRef.current) { connectionRef.current.stop(); connectionRef.current = null; }
            setNotifications([]);
            return;
        }

        // Avoid duplicate connections
        if (connectionRef.current) return;

        const connection = createNotificationConnection(token);
        let stopped = false;

        connection.on("ReceiveNotification", (notification) => {
            setNotifications(prev => [notification, ...prev]);
        });

        connection.on("ConnectionStatusChanged", () => {
            setConnectionStatusVersion(prev => prev + 1);
        });

        connection.on("ReceiveChatMessage", (message) => {
            setLatestChatMessage(message || null);
            // setChatMessageVersion(prev => [message, ...prev]);
            setChatMessageVersion(prev => prev + 1);
        });

        connection.on("ReceiveTypingIndicator", (payload) => {
            setLatestTypingIndicator(payload || null);
            setTypingIndicatorVersion((v) => v + 1);
        });

        connection.on("MessagesSeen", (payload) => {
            setLatestMessageSeen(payload || null);
            setMessageSeenVersion((v) => v + 1);
        });

        connection.on("MessagesDeleted", (payload) => {
            setLatestMessagesDeleted(payload || null);
            setMessagesDeletedVersion((v) => v + 1);
        });

        // ── Call signal listeners ────────────────────────────────────

        // Receiver gets this when someone calls them
        // → show incoming call popup
        // connection.on("ReceiveCallOffer", (payload) => {
        //     setIncomingCall(payload || null);
        // });
        connection.on("ReceiveCallOffer", (payload) => {
            // Normalize both PascalCase and camelCase
            const normalized = {
                callUUID: payload?.CallUUID ?? payload?.callUUID,
                conversationUUID: payload?.ConversationUUID ?? payload?.conversationUUID,
                senderUserId: payload?.SenderUserId ?? payload?.senderUserId,
                callerName: payload?.CallerName ?? payload?.callerName,
                callerImg: payload?.CallerImg ?? payload?.callerImg,
                sdpOffer: payload?.SdpOffer ?? payload?.sdpOffer,
                calledAt: payload?.CalledAt ?? payload?.calledAt,
            };

            if (normalized.sdpOffer) {
                // SDP arrived from hub — store separately for CallWindow
                setLatestCallOffer(normalized);
                // Also update incomingCall if it matches same callUUID
                setIncomingCall((prev) =>
                    prev?.callUUID === normalized.callUUID
                        ? { ...prev, sdpOffer: normalized.sdpOffer }
                        : prev
                );
            } else {
                // Initial notification from controller (no SDP yet)
                setIncomingCall(normalized);
            }
        });

        // Caller gets this when receiver accepts or rejects
        // → if accepted: start WebRTC answer flow
        // → if rejected: close call UI
        // connection.on("ReceiveCallAnswer", (payload) => {
        //     setLatestCallAnswer(payload || null);
        //     setCallAnswerVersion((v) => v + 1);
        // });
        connection.on("ReceiveCallAnswer", (payload) => {
            const normalized = {
                callUUID: payload?.CallUUID ?? payload?.callUUID,
                conversationUUID: payload?.ConversationUUID ?? payload?.conversationUUID,
                senderUserId: payload?.SenderUserId ?? payload?.senderUserId,
                accepted: payload?.Accepted ?? payload?.accepted,
                sdpAnswer: payload?.SdpAnswer ?? payload?.sdpAnswer,
                answeredAt: payload?.AnsweredAt ?? payload?.answeredAt,
            };
            setLatestCallAnswer(normalized);
            setCallAnswerVersion((v) => v + 1);
        });
        // Both peers get ICE candidates from each other
        // → add to RTCPeerConnection
        // connection.on("ReceiveIceCandidate", (payload) => {
        //     setLatestIceCandidate(payload || null);
        //     setIceCandidateVersion((v) => v + 1);
        // });
        connection.on("ReceiveIceCandidate", (payload) => {
            const normalized = {
                callUUID: payload?.CallUUID ?? payload?.callUUID,
                conversationUUID: payload?.ConversationUUID ?? payload?.conversationUUID,
                senderUserId: payload?.SenderUserId ?? payload?.senderUserId,
                candidate: payload?.Candidate ?? payload?.candidate,
            };
            setLatestIceCandidate(normalized);
            setIceCandidates((prev) => [...prev.slice(-99), normalized]);
            setIceCandidateVersion((v) => v + 1);
        });
        // Both peers get this when call ends (hung up, missed, rejected)
        // → close call UI on both sides
        // connection.on("CallEnded", (payload) => {
        //     setLatestCallEnded(payload || null);
        //     setCallEndedVersion((v) => v + 1);
        //     // Also clear incoming call if it was never answered
        //     setIncomingCall(null);
        // });
        connection.on("CallEnded", (payload) => {
            const normalized = {
                callUUID: payload?.CallUUID ?? payload?.callUUID,
                endedByUserId: payload?.EndedByUserId ?? payload?.endedByUserId,
                endedAt: payload?.EndedAt ?? payload?.endedAt,
            };
            setLatestCallEnded(normalized);
            setCallEndedVersion((v) => v + 1);
            setIncomingCall(null);
        });
        // Both peers get this after call ends
        // → append call log item into chat timeline in real time
        connection.on("ReceiveCallLog", (payload) => {
            setLatestCallLog(payload || null);
            setCallLogVersion((v) => v + 1);
        });

        // ────────────────────────────────────────────────────────────

        connection.on("ModerationCheckResult", (payload) => {
            const normalized = {
                field: payload?.Field ?? payload?.field,
                passed: payload?.Passed ?? payload?.passed,
                reason: payload?.Reason ?? payload?.reason,
                checkedAt: payload?.CheckedAt ?? payload?.checkedAt,
            };
            setLatestModerationCheck(normalized);
            setModerationCheckVersion(v => v + 1);
        });

        connection.start()
            .then(() => {
                if (stopped) { connection.stop(); return; }
            })
            .catch(err => {
                if (!stopped) console.error("[SignalR] Failed:", err);
            });

        connectionRef.current = connection;

        return () => {
            stopped = true;
            connection.stop();
            connectionRef.current = null;
        };
    }, [token]); // only reconnects if token changes (login/logout)

    const invokeTypingIndicator = useCallback(
        async (receiverUserId, conversationUUID, isTyping) => {
            const conn = connectionRef.current;
            if (
                !conn ||
                conn.state !== signalR.HubConnectionState.Connected ||
                !receiverUserId ||
                receiverUserId <= 0
            ) {
                return;
            }
            try {
                await conn.invoke(
                    "SendTypingIndicator",
                    receiverUserId,
                    conversationUUID ?? "",
                    Boolean(isTyping)
                );
            } catch {
                // ignore — connection can drop between check and invoke
            }
        },
        []
    );

    // ── Call invokes ─────────────────────────────────────────────────

    // Caller fires this after creating SDP offer
    const invokeSendCallOffer = useCallback(
        async (receiverUserId, conversationUUID, sdpOffer, callUUID) => {
            const conn = connectionRef.current;
            if (!conn || conn.state !== signalR.HubConnectionState.Connected) return;
            try {
                await conn.invoke(
                    "SendCallOffer",
                    receiverUserId,
                    conversationUUID ?? "",
                    sdpOffer,
                    callUUID
                );
            } catch {
                // ignore
            }
        },
        []
    );

    // Receiver fires this after accepting (with SDP answer) or rejecting
    const invokeSendCallAnswer = useCallback(
        async (callerUserId, conversationUUID, callUUID, accepted, sdpAnswer) => {
            const conn = connectionRef.current;
            if (!conn || conn.state !== signalR.HubConnectionState.Connected) return;
            try {
                await conn.invoke(
                    "SendCallAnswer",
                    callerUserId,
                    conversationUUID ?? "",
                    callUUID,
                    Boolean(accepted),
                    sdpAnswer ?? null
                );
            } catch {
                // ignore
            }
        },
        []
    );

    // Both peers fire this as ICE candidates are discovered
    const invokeSendIceCandidate = useCallback(
        async (receiverUserId, conversationUUID, callUUID, candidate) => {
            const conn = connectionRef.current;
            if (!conn || conn.state !== signalR.HubConnectionState.Connected) return;
            try {
                await conn.invoke(
                    "SendIceCandidate",
                    receiverUserId,
                    conversationUUID ?? "",
                    callUUID,
                    candidate
                );
            } catch {
                // ignore
            }
        },
        []
    );

    // Either peer fires this to hang up
    const invokeEndCall = useCallback(
        async (receiverUserId, conversationUUID, callUUID) => {
            const conn = connectionRef.current;
            if (!conn || conn.state !== signalR.HubConnectionState.Connected) return;
            try {
                await conn.invoke(
                    "EndCall",
                    receiverUserId,
                    conversationUUID ?? "",
                    callUUID
                );
            } catch {
                // ignore
            }
        },
        []
    );

    // ────────────────────────────────────────────────────────────────

    return (
        <SignalRContext.Provider
            value={{
                notifications,
                setNotifications,
                connectionStatusVersion,
                chatMessageVersion,
                latestChatMessage,
                typingIndicatorVersion,
                latestTypingIndicator,
                messageSeenVersion,
                latestMessageSeen,
                invokeTypingIndicator,
                messagesDeletedVersion,
                latestMessagesDeleted,

                // ── Call signals ─────────────────────────────────────
                incomingCall,
                setIncomingCall,
                callAnswerVersion,
                latestCallAnswer,
                iceCandidateVersion,
                latestIceCandidate,
                iceCandidates,
                callEndedVersion,
                latestCallEnded,
                callLogVersion,
                latestCallLog,
                latestCallOffer,

                // ── Call invokes ─────────────────────────────────────
                invokeSendCallOffer,
                invokeSendCallAnswer,
                invokeSendIceCandidate,
                invokeEndCall,
                moderationCheckVersion,
                latestModerationCheck,
            }}
        >
            {children}
        </SignalRContext.Provider>
    );
};

export const useSignalR = () => useContext(SignalRContext);
